import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { useAppContext } from "../../../../../AppContext";
import { IEventWizardInitialData } from "../../models/eventWizardInitialDataModel";
import { ISelectFieldModel } from "../../../../../common/components/forms/inputs/dropdown/models/selectFieldModel";
import { PageMode } from "../../../../../common/types";
import { useParams } from "react-router";
import { EventDurationMetric } from "../../../../../common/enums";
import { useFormValidationMessages } from "../../../../../common/infrastructure/hooks/useFormHelper";
import {
  IServiceWizardFormModel,
  serviceWizardFormDefaultValues,
  serviceWizardFormSchema,
} from "../models/serviceWizardFormModel";

export const useServiceWizardForm = (
  selectedDate?: Date,
  studioEmployees?: ISelectFieldModel[],
  initialData?: IEventWizardInitialData,
  pageMode?: PageMode
) => {
  const { selectedStudioId, currentUser } = useAppContext();
  const { id } = useParams();

  const form = useForm<IServiceWizardFormModel>({
    defaultValues: serviceWizardFormDefaultValues,
    resolver: yupResolver(serviceWizardFormSchema) as any,
  });

  // The initial data is refetched while the wizard is open: on window focus, and
  // right after a client is added from this wizard. React Query keeps the
  // references of the parts that did not change, so the effects below depend on
  // the parts, not on the whole object. A refetch that only brings a new client
  // must not reset the service, time or client the admin already picked.
  const allAvailableUsers = initialData?.allAvailableUsers;
  const services = initialData?.services;
  const currentlySelectedEvent = initialData?.currentlySelectedEvent;
  const studioSettings = initialData?.studioSettings;

  // A new reservation defaults to the first client, but keeps the current one
  // while it is still in the list (a client just added from this wizard is
  // selected before the refetched list arrives, and is in it when it does).
  // An edited or duplicated reservation takes its client from the event below.
  useEffect(() => {
    if (
      pageMode === "edit" ||
      currentlySelectedEvent ||
      !allAvailableUsers?.length
    ) {
      return;
    }

    const currentUserId = form.getValues("userId");
    if (!allAvailableUsers.some((user) => user.id === currentUserId)) {
      form.setValue("userId", allAvailableUsers[0].id);
    }
  }, [allAvailableUsers, currentlySelectedEvent, pageMode, form]);

  useEffect(() => {
    if (pageMode !== "edit" && services && services.length > 0) {
      const firstService = services[0];
      form.setValue("serviceId", firstService.id);
      form.setValue("durationInMinutes", firstService.durationInMinutes);
      form.setValue("price", firstService.price);
    }

    if (currentlySelectedEvent) {
      if (pageMode !== "duplicate") {
        form.setValue("id", Number(id));
      }

      form.setValue("serviceId", currentlySelectedEvent.serviceId || 0);
      form.setValue("employeeId", currentlySelectedEvent.employeeId || 0);
      form.setValue("userId", currentlySelectedEvent.individualUserId || 0);
      form.setValue(
        "durationInMinutes",
        currentlySelectedEvent.durationInMinutes
      );
      form.setValue("price", currentlySelectedEvent.price);
      form.setValue("date", new Date(currentlySelectedEvent.startTime));
      form.setValue(
        "isMultisportAvailable",
        currentlySelectedEvent.isMultisportAvailable
      );
      form.setValue(
        "forceEmailNotifications",
        currentlySelectedEvent.forceEmailNotifications
      );
      form.setValue("isRecurring", currentlySelectedEvent.isRecurring);
    } else if (studioSettings) {
      // Set default values from tenant settings
      form.setValue(
        "isMultisportAvailable",
        studioSettings.isMultisportAvailable
      );

      if (!services || services.length === 0) {
        form.setValue("durationInMinutes", EventDurationMetric.OneHour);
      }

      if (selectedDate && pageMode !== "add") {
        form.setValue("date", selectedDate);
      }
    }

    if (pageMode !== "edit" && studioEmployees && studioEmployees.length > 0) {
      const currentEmployeeId = form.getValues("employeeId");
      if (!currentEmployeeId) {
        const currentUserEmployee = studioEmployees.find(
          (emp) => emp.id === currentUser?.id
        );
        const defaultEmployeeId = currentUserEmployee
          ? currentUserEmployee.id
          : studioEmployees[0].id;
        form.setValue("employeeId", defaultEmployeeId);
      }
    }

    // Always set studio ID
    if (selectedStudioId) {
      form.setValue("studioId", selectedStudioId);
    }
  }, [
    selectedStudioId,
    selectedDate,
    services,
    currentlySelectedEvent,
    studioSettings,
    studioEmployees,
    pageMode,
    id,
    form,
  ]);

  useFormValidationMessages<IServiceWizardFormModel>(form);

  return form;
};
